import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { supabase } from '../lib/supabase';
import Sidebar from '../components/Sidebar';

const MONTHS = ['Jan', 'Feb', 'Mar', 'Apr', 'Mai', 'Jun', 'Jul', 'Aug', 'Sep', 'Okt', 'Nov', 'Des'];
const MONTH_FULL = [
  'Januar', 'Februar', 'Mars', 'April', 'Mai', 'Juni',
  'Juli', 'August', 'September', 'Oktober', 'November', 'Desember',
];

function fmtAmount(v) {
  if (v == null) return '—';
  return Number(v).toLocaleString('nb-NO', { minimumFractionDigits: 2, maximumFractionDigits: 2 });
}

function groupPostingsByDesc(postings) {
  const groups = {};
  for (const p of postings) {
    const desc = p.description || '—';
    if (!groups[desc]) {
      groups[desc] = { description: desc, count: 0, total: 0, postings: [] };
    }
    groups[desc].count += 1;
    groups[desc].total += Number(p.amount) || 0;
    groups[desc].postings.push(p);
  }
  return Object.values(groups).sort((a, b) => b.total - a.total);
}

export default function KontantAvstemming() {
  const [isDark, setIsDark] = useState(true);
  const [user, setUser]     = useState(null);
  const navigate            = useNavigate();

  const [entities, setEntities]             = useState([]);
  const [selectedEntity, setSelectedEntity] = useState(null);
  const [loading, setLoading]               = useState(true);

  const [year, setYear]                   = useState(new Date().getFullYear());
  const [selectedMonth, setSelectedMonth] = useState(null); // null = Alle
  const [ytd, setYtd]                     = useState(false); // year-to-date: cumulative t.o.m. selectedMonth
  const [amountFilter, setAmountFilter]   = useState('all'); // 'all' | 'positive' | 'negative'
  const [rawData, setRawData]             = useState(null);
  const [loadingData, setLoadingData]     = useState(false);
  const [departments, setDepartments]     = useState({});
  const [contacts, setContacts]           = useState({});
  const [accounts, setAccounts]           = useState({});  // keyed by tripletex_id
  const [bookingCashLoss, setBookingCashLoss] = useState(null); // deptId being booked
  const [expandedDept, setExpandedDept]   = useState(null);
  const [savingOverride, setSavingOverride] = useState(null);
  const [savingRecon, setSavingRecon]       = useState(null);
  const [fadingOut, setFadingOut]           = useState(new Set()); // posting tripletex_ids being animated out
  const [selectedPostings, setSelectedPostings] = useState(new Set()); // checked posting tripletex_ids
  const [bulkTarget, setBulkTarget]       = useState(''); // 'year_month' string for mass move
  const [bulkSaving, setBulkSaving]       = useState(false);
  const [groupByDesc, setGroupByDesc]     = useState(false);
  const [expandAll, setExpandAll]         = useState(false);
  const [showOpenSpec, setShowOpenSpec]   = useState(false);
  const [openEntriesData, setOpenEntriesData] = useState([]);

  const bg       = isDark ? 'bg-slate-950 text-slate-50'  : 'bg-slate-50 text-slate-900';
  const card     = isDark ? 'bg-slate-900/40 border-slate-800' : 'bg-white border-slate-200';
  const section  = isDark ? 'border-slate-800' : 'border-slate-200';
  const btnSmall = isDark
    ? 'bg-slate-800 hover:bg-slate-700 border border-slate-700 text-slate-300'
    : 'bg-slate-100 hover:bg-slate-200 border border-slate-300 text-slate-700';
  const muted    = isDark ? 'text-slate-500' : 'text-slate-400';
  const rowHover = isDark ? 'hover:bg-slate-800/60' : 'hover:bg-slate-50';
  const subRow   = isDark ? 'bg-slate-900/60' : 'bg-slate-50/80';

  // YTD shows an aggregate (Alle-style) view, cumulative up to and including selectedMonth.
  // singleMonth = the classic single-month view; effMonth drives all single-month-only UI.
  const singleMonth = selectedMonth !== null && !ytd;
  const effMonth    = singleMonth ? selectedMonth : null;

  const years = [];
  for (let y = 2024; y <= new Date().getFullYear(); y++) years.push(y);

  /* ── Auth ─────────────────────────────────────────────── */
  useEffect(() => {
    supabase.auth.getSession().then(({ data: { session } }) => {
      setUser(session?.user ?? null);
      if (!session) navigate('/login', { replace: true });
    });
    const { data: { subscription } } = supabase.auth.onAuthStateChange((_e, session) => {
      const next = session?.user ?? null;
      setUser(prev => (prev?.id === next?.id ? prev : next));
      if (!session) navigate('/login', { replace: true });
    });
    return () => subscription.unsubscribe();
  }, [navigate]);

  /* ── Entities ─────────────────────────────────────────── */
  const loadEntities = useCallback(async () => {
    if (!user) return;
    setLoading(true);
    const { data } = await supabase
      .from('user_legal_entity_access')
      .select(`role, legal_entity:legal_entities (id, name, organization_number, cash_account_tripletex_id, cash_loss_account_tripletex_id, tripletex_integration:tripletex_integrations (token_status, is_active))`)
      .eq('user_id', user.id);
    if (data) {
      const list = data
        .map(row => ({ ...row.legal_entity, role: row.role }))
        .filter(e => e.tripletex_integration?.token_status === 'valid');
      setEntities(list);
      if (list.length > 0 && !selectedEntity) setSelectedEntity(list[0].id);
    }
    setLoading(false);
  }, [user, selectedEntity]);

  useEffect(() => { loadEntities(); }, [loadEntities]);

  /* ── Lookups ──────────────────────────────────────────── */
  const loadLookups = useCallback(async (entityId) => {
    const [deptRes, contRes, accRes] = await Promise.all([
      supabase.from('tripletex_departments').select('tripletex_id, name, department_number').eq('legal_entity_id', entityId),
      supabase.from('tripletex_contacts').select('tripletex_id, name').eq('legal_entity_id', entityId),
      supabase.from('tripletex_accounts').select('tripletex_id, number, name').eq('legal_entity_id', entityId),
    ]);
    if (deptRes.data) {
      const map = {};
      deptRes.data.forEach(d => { map[d.tripletex_id] = d; });
      setDepartments(map);
    }
    if (contRes.data) {
      const map = {};
      contRes.data.forEach(c => { map[c.tripletex_id] = c; });
      setContacts(map);
    }
    if (accRes.data) {
      const map = {};
      accRes.data.forEach(a => { map[a.tripletex_id] = a; });
      setAccounts(map);
    }
  }, []);

  /* ── RPC data ─────────────────────────────────────────── */
  const loadData = useCallback(async (silent = false) => {
    if (!selectedEntity) return;
    if (!silent) setLoadingData(true);
    await loadLookups(selectedEntity);
    const cashAccountId = entities.find(e => e.id === selectedEntity)?.cash_account_tripletex_id ?? null;
    const [reconRes, openRes] = await Promise.all([
      supabase.rpc('get_cash_reconciliation', {
        p_legal_entity_id: selectedEntity,
        p_year: year,
        p_cash_account_tripletex_id: cashAccountId,
      }),
      supabase.rpc('get_open_cash_entries', {
        p_legal_entity_id: selectedEntity,
        p_year: year,
        p_month: selectedMonth,
        p_cash_account_tripletex_id: cashAccountId,
      }),
    ]);
    if (reconRes.error) {
      console.error('RPC error:', reconRes.error);
      if (!silent) setRawData(null);
    } else {
      setRawData(reconRes.data);
    }
    if (openRes.error) {
      console.error('Open entries RPC error:', openRes.error);
    } else {
      setOpenEntriesData(openRes.data || []);
    }
    if (!silent) setLoadingData(false);
  }, [selectedEntity, year, selectedMonth, loadLookups, entities]);

  useEffect(() => { loadData(); }, [loadData]);

  /* ── Derived data ─────────────────────────────────────── */
  const { deptRows, monthTotals, totalPostings, periodMap } = useMemo(() => {
    if (!rawData) return { deptRows: [], monthTotals: {}, totalPostings: 0, periodMap: {} };

    const allPostings = rawData.postings || [];
    const periods = rawData.periods || [];

    // Build period lookup
    const pMap = {};
    for (const p of periods) {
      pMap[`${p.department_tripletex_id}_${p.recon_month}`] = p;
    }

    // Filter postings by individual amount sign
    let filteredPostings = allPostings;
    if (amountFilter === 'positive') {
      filteredPostings = allPostings.filter(p => Number(p.amount) > 0);
    } else if (amountFilter === 'negative') {
      filteredPostings = allPostings.filter(p => Number(p.amount) < 0);
    }

    // Aggregate by department+month from filtered postings
    const deptMonthMap = {};
    for (const p of filteredPostings) {
      const deptId = p.department_tripletex_id;
      const month = p.recon_month;
      const key = `${deptId}_${month}`;
      if (!deptMonthMap[key]) {
        deptMonthMap[key] = {
          department_tripletex_id: deptId,
          recon_month: month,
          total_debit: 0,
          total_credit: 0,
          total: 0,
          posting_count: 0,
        };
      }
      const amount = Number(p.amount) || 0;
      if (amount > 0) deptMonthMap[key].total_debit += amount;
      else deptMonthMap[key].total_credit += amount;
      deptMonthMap[key].total += amount;
      deptMonthMap[key].posting_count += 1;
      if (p.booked_by_app) {
        deptMonthMap[key].app_booked_total = (deptMonthMap[key].app_booked_total || 0) + amount;
      }
    }

    // Group by department across months for the "Alle" / year-to-date view.
    // In YTD mode, only include months up to and including selectedMonth.
    const deptAggMap = {};
    for (const row of Object.values(deptMonthMap)) {
      if (ytd && selectedMonth !== null && row.recon_month > selectedMonth) continue;
      const deptId = row.department_tripletex_id;
      if (!deptAggMap[deptId]) {
        deptAggMap[deptId] = {
          department_tripletex_id: deptId,
          total_debit: 0,
          total_credit: 0,
          total: 0,
          posting_count: 0,
          months: {},
        };
      }
      deptAggMap[deptId].total_debit += row.total_debit;
      deptAggMap[deptId].total_credit += row.total_credit;
      deptAggMap[deptId].total += row.total;
      deptAggMap[deptId].posting_count += row.posting_count;
      deptAggMap[deptId].app_booked_total = (deptAggMap[deptId].app_booked_total || 0) + (row.app_booked_total || 0);
      deptAggMap[deptId].months[row.recon_month] = row;
    }

    // Monthly totals
    const mTotals = {};
    for (const row of Object.values(deptMonthMap)) {
      if (!mTotals[row.recon_month]) {
        mTotals[row.recon_month] = { debit: 0, credit: 0, total: 0, count: 0 };
      }
      mTotals[row.recon_month].debit += row.total_debit;
      mTotals[row.recon_month].credit += row.total_credit;
      mTotals[row.recon_month].total += row.total;
      mTotals[row.recon_month].count += row.posting_count;
    }

    // Filtered department rows. Aggregate for "Alle" and YTD; per-month otherwise.
    let rows;
    if (selectedMonth === null || ytd) {
      rows = Object.values(deptAggMap);
    } else {
      rows = Object.values(deptMonthMap).filter(r => r.recon_month === selectedMonth);
    }

    rows.sort((a, b) => {
      const nameA = departments[a.department_tripletex_id]?.name ?? '';
      const nameB = departments[b.department_tripletex_id]?.name ?? '';
      return nameA.localeCompare(nameB, 'nb');
    });

    // In YTD mode, count only postings up to and including selectedMonth.
    const scopedCount = (ytd && selectedMonth !== null)
      ? filteredPostings.filter(p => p.recon_month <= selectedMonth).length
      : filteredPostings.length;

    return {
      deptRows: rows,
      monthTotals: mTotals,
      totalPostings: scopedCount,
      periodMap: pMap,
    };
  }, [rawData, selectedMonth, ytd, amountFilter, departments]);

  /* ── Per-dept posting helper ──────────────────────────── */
  const getPostingsForDept = useCallback((deptId) => {
    if (!rawData) return [];
    const postings = rawData.postings || [];
    return postings.filter(p => {
      if (fadingOut.has(p.tripletex_id)) return false;
      if (p.department_tripletex_id !== deptId) return false;
      if (selectedMonth !== null) {
        if (ytd) { if (p.recon_month > selectedMonth) return false; }
        else if (p.recon_month !== selectedMonth) return false;
      }
      if (amountFilter === 'positive' && Number(p.amount) <= 0) return false;
      if (amountFilter === 'negative' && Number(p.amount) >= 0) return false;
      return true;
    });
  }, [rawData, selectedMonth, ytd, fadingOut, amountFilter]);

  /* ── All filtered postings (for general select-all) ─── */
  const allFilteredPostings = useMemo(() => {
    if (!rawData) return [];
    const postings = rawData.postings || [];
    return postings.filter(p => {
      if (fadingOut.has(p.tripletex_id)) return false;
      if (selectedMonth !== null) {
        if (ytd) { if (p.recon_month > selectedMonth) return false; }
        else if (p.recon_month !== selectedMonth) return false;
      }
      if (amountFilter === 'positive' && Number(p.amount) <= 0) return false;
      if (amountFilter === 'negative' && Number(p.amount) >= 0) return false;
      return true;
    });
  }, [rawData, selectedMonth, ytd, amountFilter, fadingOut]);

  /* ── Optimistic move: animate out then refresh silently ── */
  async function performMove(tripletexIds, newYear, newMonth) {
    if (!user || !tripletexIds.length) return;

    // 1. Fade out
    setFadingOut(prev => {
      const next = new Set(prev);
      tripletexIds.forEach(id => next.add(id));
      return next;
    });

    // 2. Wait for CSS transition
    await new Promise(r => setTimeout(r, 300));

    // 3. Write overrides
    const upserts = [];
    const deletes = [];
    const allPostings = rawData?.postings || [];

    for (const tid of tripletexIds) {
      const posting = allPostings.find(p => p.tripletex_id === tid);
      if (!posting) continue;
      if (newMonth === posting.natural_month && newYear === posting.natural_year) {
        deletes.push(tid);
      } else {
        upserts.push({
          legal_entity_id: selectedEntity,
          posting_tripletex_id: tid,
          recon_year: newYear,
          recon_month: newMonth,
          created_by: user.id,
        });
      }
    }

    const promises = [];
    if (upserts.length) {
      promises.push(
        supabase
          .from('cash_recon_overrides')
          .upsert(upserts, { onConflict: 'legal_entity_id,posting_tripletex_id' })
      );
    }
    for (const tid of deletes) {
      promises.push(
        supabase
          .from('cash_recon_overrides')
          .delete()
          .eq('legal_entity_id', selectedEntity)
          .eq('posting_tripletex_id', tid)
      );
    }
    await Promise.all(promises);

    // 4. Clear selection
    setSelectedPostings(new Set());

    // 5. Silent refresh (no loading flash)
    await loadData(true);
    setFadingOut(new Set());
  }

  /* ── Single posting move ─────────────────────────────── */
  async function handleMonthOverride(posting, newYear, newMonth) {
    setSavingOverride(posting.tripletex_id);
    await performMove([posting.tripletex_id], newYear, newMonth);
    setSavingOverride(null);
  }

  /* ── Bulk move ───────────────────────────────────────── */
  async function handleBulkMove() {
    if (!bulkTarget || selectedPostings.size === 0) return;
    const [y, m] = bulkTarget.split('_').map(Number);
    setBulkSaving(true);
    await performMove([...selectedPostings], y, m);
    setBulkSaving(false);
    setBulkTarget('');
  }

  /* ── Reconciliation toggle ────────────────────────────── */
  async function handleToggleRecon(deptId, month, groupTotal) {
    if (!user) return;
    const key = `${deptId}_${month}`;

    const existing = periodMap[key];
    const newStatus = existing?.status === 'reconciled' ? 'open' : 'reconciled';

    // Prevent closing unless sum is 0
    if (newStatus === 'reconciled' && Math.abs(groupTotal) > 0.005) {
      return;
    }

    setSavingRecon(key);

    await supabase
      .from('cash_recon_periods')
      .upsert({
        legal_entity_id: selectedEntity,
        department_tripletex_id: deptId,
        recon_year: year,
        recon_month: month,
        status: newStatus,
        reconciled_by: newStatus === 'reconciled' ? user.id : null,
        reconciled_at: newStatus === 'reconciled' ? new Date().toISOString() : null,
      }, { onConflict: 'legal_entity_id,department_tripletex_id,recon_year,recon_month' });

    setSavingRecon(null);
    await loadData(true);
  }

  /* ── Book cash loss ──────────────────────────────────── */
  async function handleBookCashLoss(deptId, deptPostings, totalDebit, totalCredit) {
    if (!user || !selectedEntity || !singleMonth) return;

    const netto = totalDebit + totalCredit;
    if (Math.abs(netto) < 0.005) return;

    const entityObj = entities.find(e => e.id === selectedEntity);
    const cashAccountId = entityObj?.cash_account_tripletex_id
      ?? Object.values(accounts).find(a => a.number === '1900')?.tripletex_id;
    const lossAccountId = entityObj?.cash_loss_account_tripletex_id
      ?? Object.values(accounts).find(a => a.number === '7798')?.tripletex_id;
    if (!cashAccountId || !lossAccountId) {
      alert('Kontantkontoer er ikke konfigurert for denne enheten. Gå til Enheter og velg kontoer for kontantavstemming.');
      return;
    }

    const entityName = entityObj?.name ?? '';
    const deptName = resolveDept(deptId);
    const monthName = MONTH_FULL[selectedMonth - 1];
    const voucherDate = new Date().toISOString().slice(0, 10);
    const description = `Kontantsvinn ${deptName} ${monthName} ${year}`;
    const lineDescription = `Kontantsvinn ${monthName} ${year}`;

    setBookingCashLoss(deptId);

    try {
      const { error } = await supabase.rpc('book_cash_loss', {
        p_legal_entity_id: selectedEntity,
        p_department_tripletex_id: deptId,
        p_recon_year: year,
        p_recon_month: selectedMonth,
        p_voucher_date: voucherDate,
        p_description: description,
        p_netto: netto,
        p_acc1900_tripletex_id: cashAccountId,
        p_acc7798_tripletex_id: lossAccountId,
        p_line_description: lineDescription,
        p_metadata: {
          type: 'cash_loss',
          entity_name: entityName,
          department_name: deptName,
          month_name: monthName,
          year,
          postings: deptPostings.map(p => ({
            date: p.date,
            voucher_number: p.voucher_number,
            description: p.description,
            amount: Number(p.amount),
          })),
          total_debit: totalDebit,
          total_credit: totalCredit,
          cash_loss: netto,
        },
      });

      if (error) throw error;

      await loadData(true);
    } catch (err) {
      console.error('Cash loss booking failed:', err);
      alert(`Feil ved bokføring: ${err.message}`);
    } finally {
      setBookingCashLoss(null);
    }
  }

  /* ── Selection helpers ────────────────────────────────── */
  function togglePosting(tripletexId) {
    setSelectedPostings(prev => {
      const next = new Set(prev);
      next.has(tripletexId) ? next.delete(tripletexId) : next.add(tripletexId);
      return next;
    });
  }

  function toggleAllInDept(deptPostings) {
    const deptIds = deptPostings.map(p => p.tripletex_id);
    const allSelected = deptIds.length > 0 && deptIds.every(id => selectedPostings.has(id));
    setSelectedPostings(prev => {
      const next = new Set(prev);
      if (allSelected) {
        deptIds.forEach(id => next.delete(id));
      } else {
        deptIds.forEach(id => next.add(id));
      }
      return next;
    });
  }

  function toggleAllVisible() {
    const allIds = allFilteredPostings.map(p => p.tripletex_id);
    const allSelected = allIds.length > 0 && allIds.every(id => selectedPostings.has(id));
    if (allSelected) {
      setSelectedPostings(new Set());
    } else {
      setSelectedPostings(new Set(allIds));
    }
  }

  // Clear selection when expanding a different department
  function handleToggleDept(row) {
    if (expandAll) return; // individual toggle disabled when expand-all is on
    const deptId = row.department_tripletex_id;
    const isExp = expandedDept?.department_tripletex_id === deptId
      && (effMonth === null || expandedDept?.recon_month === row.recon_month);
    setSelectedPostings(new Set());
    setBulkTarget('');
    setExpandedDept(isExp ? null : row);
  }

  /* ── Helpers ──────────────────────────────────────────── */
  function resolveDept(tripletexId) {
    if (!tripletexId) return '—';
    const d = departments[tripletexId];
    return d ? `${d.department_number ?? ''} ${d.name}`.trim() : `#${tripletexId}`;
  }

  function resolveContact(custId, suppId) {
    const id = custId || suppId;
    if (!id) return null;
    return contacts[id]?.name ?? null;
  }

  function isReconciled(deptId, month) {
    if (singleMonth) {
      return periodMap[`${deptId}_${month}`]?.status === 'reconciled';
    }
    return false;
  }

  // Count reconciled departments for selected month (single-month view only)
  const reconStats = useMemo(() => {
    if (!singleMonth || !deptRows.length) return null;
    const total = deptRows.length;
    const reconciled = deptRows.filter(r =>
      periodMap[`${r.department_tripletex_id}_${selectedMonth}`]?.status === 'reconciled'
    ).length;
    return { reconciled, total };
  }, [singleMonth, selectedMonth, deptRows, periodMap]);

  /* ── Open entries specification (from server RPC) ──────── */
  const openEntries = useMemo(() => {
    if (!openEntriesData || !openEntriesData.length) return [];
    return [...openEntriesData].sort((a, b) => {
      if (a.recon_year !== b.recon_year) return a.recon_year - b.recon_year;
      if (a.recon_month !== b.recon_month) return a.recon_month - b.recon_month;
      const nameA = departments[a.department_tripletex_id]?.name ?? '';
      const nameB = departments[b.department_tripletex_id]?.name ?? '';
      return nameA.localeCompare(nameB, 'nb');
    });
  }, [openEntriesData, departments]);

  const openEntriesTotalAmount = useMemo(() =>
    openEntries.reduce((s, e) => s + Number(e.total), 0),
    [openEntries]
  );

  if (!user) return null;

  const hasSelection = selectedPostings.size > 0;

  return (
    <div className={`min-h-screen flex ${bg}`}>
      <Sidebar
        isDark={isDark}
        setIsDark={setIsDark}
        activePage="/kontant-avstemming"
        entities={entities}
        selectedEntity={selectedEntity}
        onSelectEntity={setSelectedEntity}
      />

      <main className="flex-1 p-8 overflow-y-auto">
        <h2 className="font-bbhbogle text-2xl tracking-[0.1em] mb-2">Kontantavstemming</h2>
        <p className={`font-mono text-xs mb-8 ${muted}`}>Konto 1900 – Avstemming pr. avdeling og måned</p>

        {loading && <p className={`font-mono text-xs ${muted}`}>Laster...</p>}
        {!loading && entities.length === 0 && (
          <p className={`font-mono text-xs ${muted}`}>Ingen enheter med gyldig Tripletex-integrasjon funnet.</p>
        )}

        {selectedEntity && (
          <div className="max-w-6xl">

            {/* Controls */}
            <div className="flex items-center gap-4 mb-6 flex-wrap">
              <select
                value={year}
                onChange={e => { setYear(Number(e.target.value)); setExpandedDept(null); setSelectedPostings(new Set()); }}
                className={`font-mono text-xs px-3 py-1.5 rounded border outline-none cursor-pointer ${
                  isDark ? 'bg-slate-800 border-slate-700 text-slate-200' : 'bg-white border-slate-300 text-slate-800'
                }`}
              >
                {years.map(y => <option key={y} value={y}>{y}</option>)}
              </select>

              {/* Month pills */}
              <div className="flex gap-0.5 flex-wrap">
                <button
                  onClick={() => { setSelectedMonth(null); setYtd(false); setExpandedDept(null); setSelectedPostings(new Set()); }}
                  className={`font-mono text-[10px] px-2.5 py-1 rounded transition-colors ${
                    selectedMonth === null
                      ? isDark ? 'bg-slate-700 text-slate-100' : 'bg-slate-800 text-white'
                      : btnSmall
                  }`}
                >
                  Alle
                </button>
                {MONTHS.map((m, i) => {
                  const monthNum = i + 1;
                  const hasData = monthTotals[monthNum];
                  return (
                    <button
                      key={monthNum}
                      onClick={() => { setSelectedMonth(monthNum); setExpandedDept(null); setSelectedPostings(new Set()); }}
                      className={`font-mono text-[10px] px-2.5 py-1 rounded transition-colors ${
                        selectedMonth === monthNum
                          ? isDark ? 'bg-slate-700 text-slate-100' : 'bg-slate-800 text-white'
                          : hasData ? btnSmall : `${btnSmall} opacity-40`
                      }`}
                    >
                      {m}
                    </button>
                  );
                })}
              </div>

              {/* Year-to-date toggle */}
              <label
                className={`flex items-center gap-1.5 font-mono text-[10px] px-2.5 py-1 rounded cursor-pointer select-none transition-colors ${
                  ytd
                    ? isDark ? 'bg-slate-700 text-slate-100' : 'bg-slate-800 text-white'
                    : btnSmall
                }`}
                title="Vis akkumulert t.o.m. valgt måned"
              >
                <input
                  type="checkbox"
                  checked={ytd}
                  onChange={() => {
                    setYtd(v => {
                      const next = !v;
                      if (next && selectedMonth === null) {
                        setSelectedMonth(
                          year === new Date().getFullYear() ? new Date().getMonth() + 1 : 12
                        );
                      }
                      return next;
                    });
                    setExpandedDept(null);
                    setSelectedPostings(new Set());
                  }}
                  className="accent-current w-3 h-3"
                />
                Hittil i år
              </label>

              {/* Amount filter */}
              <div className="flex gap-0.5">
                {[
                  { key: 'all', label: 'Alle' },
                  { key: 'positive', label: '+ Debet' },
                  { key: 'negative', label: '− Kredit' },
                ].map(f => (
                  <button
                    key={f.key}
                    onClick={() => setAmountFilter(f.key)}
                    className={`font-mono text-[10px] px-2.5 py-1 rounded transition-colors ${
                      amountFilter === f.key
                        ? isDark ? 'bg-slate-700 text-slate-100' : 'bg-slate-800 text-white'
                        : btnSmall
                    }`}
                  >
                    {f.label}
                  </button>
                ))}
              </div>

              {/* Group by description toggle */}
              <button
                onClick={() => setGroupByDesc(v => !v)}
                className={`font-mono text-[10px] px-2.5 py-1 rounded transition-colors ${
                  groupByDesc
                    ? isDark ? 'bg-slate-700 text-slate-100' : 'bg-slate-800 text-white'
                    : btnSmall
                }`}
              >
                {groupByDesc ? '⊞ Gruppert' : '⊞ Grupper'}
              </button>

              {/* Expand all toggle */}
              <button
                onClick={() => {
                  if (expandAll) {
                    setExpandAll(false);
                    setExpandedDept(null);
                    setSelectedPostings(new Set());
                    setBulkTarget('');
                  } else {
                    setExpandAll(true);
                  }
                }}
                className={`font-mono text-[10px] px-2.5 py-1 rounded transition-colors ${
                  expandAll
                    ? isDark ? 'bg-slate-700 text-slate-100' : 'bg-slate-800 text-white'
                    : btnSmall
                }`}
              >
                {expandAll ? '▲ Lukk alle' : '▼ Åpne alle'}
              </button>

              <button
                onClick={() => { loadData(); setExpandedDept(null); setExpandAll(false); setSelectedPostings(new Set()); }}
                disabled={loadingData}
                className={`font-mono text-xs px-4 py-1.5 rounded transition-colors disabled:opacity-40 ${btnSmall}`}
              >
                {loadingData ? 'Laster...' : 'Oppdater'}
              </button>
            </div>

            {/* Summary card */}
            {rawData && !loadingData && (
              <div className={`rounded-xl border ${card} mb-6 overflow-hidden`}>
                <div className="p-4 flex items-center gap-8 flex-wrap">
                  <div>
                    <p className={`font-mono text-[10px] uppercase tracking-widest ${muted}`}>
                      Utestående (1900){selectedMonth !== null ? ` t.o.m. ${MONTHS[selectedMonth - 1]}` : ''}
                    </p>
                    <p className="font-mono text-lg tabular-nums mt-0.5">{fmtAmount(openEntriesTotalAmount)}</p>
                  </div>
                  <div>
                    <p className={`font-mono text-[10px] uppercase tracking-widest ${muted}`}>Poster ({year})</p>
                    <p className="font-mono text-lg tabular-nums mt-0.5">{totalPostings}</p>
                  </div>
                  <div>
                    <p className={`font-mono text-[10px] uppercase tracking-widest ${muted}`}>Avdelinger</p>
                    <p className="font-mono text-lg tabular-nums mt-0.5">{deptRows.length}</p>
                  </div>
                  {reconStats && (
                    <div>
                      <p className={`font-mono text-[10px] uppercase tracking-widest ${muted}`}>Avstemt</p>
                      <p className="font-mono text-lg tabular-nums mt-0.5">
                        <span className={reconStats.reconciled === reconStats.total ? 'text-emerald-400' : ''}>
                          {reconStats.reconciled}
                        </span>
                        <span className={muted}> / {reconStats.total}</span>
                      </p>
                    </div>
                  )}

                  {openEntries.length > 0 && (
                    <button
                      onClick={() => setShowOpenSpec(v => !v)}
                      className={`ml-auto font-mono text-[10px] px-3 py-1.5 rounded transition-colors ${
                        showOpenSpec
                          ? 'bg-amber-500/20 text-amber-400 border border-amber-500/30'
                          : isDark
                            ? 'bg-slate-800 hover:bg-slate-700 border border-slate-700 text-slate-300'
                            : 'bg-slate-100 hover:bg-slate-200 border border-slate-300 text-slate-700'
                      }`}
                    >
                      {showOpenSpec ? '▲ Skjul åpne poster' : `▼ Åpne poster (${openEntries.length})`}
                    </button>
                  )}
                </div>

                {/* Open entries specification panel */}
                {showOpenSpec && openEntries.length > 0 && (
                  <div className={`border-t ${section} px-4 py-3`}>
                    <div className="flex items-center justify-between mb-2">
                      <p className={`font-mono text-[10px] uppercase tracking-widest ${muted}`}>
                        Spesifikasjon – åpne poster
                        {selectedMonth !== null && ` (t.o.m. ${MONTH_FULL[selectedMonth - 1]})`}
                      </p>
                      <p className={`font-mono text-[10px] ${muted}`}>
                        Sum: <span className={`font-semibold ${isDark ? 'text-slate-200' : 'text-slate-800'}`}>{fmtAmount(openEntriesTotalAmount)}</span>
                      </p>
                    </div>
                    {(() => {
                      // Group entries by period for subtotals
                      const periodGroups = [];
                      let currentKey = null;
                      let currentGroup = null;
                      for (const e of openEntries) {
                        const key = `${e.recon_year}_${e.recon_month}`;
                        if (key !== currentKey) {
                          if (currentGroup) periodGroups.push(currentGroup);
                          currentKey = key;
                          currentGroup = { recon_year: e.recon_year, recon_month: e.recon_month, entries: [], totalAmount: 0, totalCount: 0 };
                        }
                        currentGroup.entries.push(e);
                        currentGroup.totalAmount += Number(e.total);
                        currentGroup.totalCount += e.posting_count;
                      }
                      if (currentGroup) periodGroups.push(currentGroup);

                      return (
                        <table className="w-full font-mono text-[11px]">
                          <thead>
                            <tr className={`${muted} border-b ${section}`}>
                              <th className="text-left py-1.5 pr-2 font-normal">Avdeling</th>
                              <th className="text-center py-1.5 pr-2 font-normal">Status</th>
                              <th className="text-center py-1.5 pr-2 font-normal">Poster</th>
                              <th className="text-right py-1.5 pr-2 font-normal">Beløp</th>
                            </tr>
                          </thead>
                          <tbody>
                            {periodGroups.map(pg => (
                              <React.Fragment key={`pg_${pg.recon_year}_${pg.recon_month}`}>
                                {/* Period header row */}
                                <tr className={`${isDark ? 'bg-slate-800/40' : 'bg-slate-50'}`}>
                                  <td colSpan={4} className="py-1.5 px-2">
                                    <span className={`text-[9px] px-2 py-0.5 rounded-full font-mono bg-amber-500/20 text-amber-400`}>
                                      {MONTH_FULL[pg.recon_month - 1]}{pg.recon_year !== year ? ` ${pg.recon_year}` : ''}
                                    </span>
                                  </td>
                                </tr>
                                {pg.entries.map(e => (
                                  <tr key={`${e.department_tripletex_id}_${e.recon_year}_${e.recon_month}`} className={`border-b ${section}`}>
                                    <td className="py-1.5 pr-2 pl-2">{resolveDept(e.department_tripletex_id)}</td>
                                    <td className="py-1.5 pr-2 text-center">
                                      <span className={`text-[9px] px-2 py-0.5 rounded-full font-mono ${
                                        e.recon_status === 'reconciled'
                                          ? 'bg-emerald-500/20 text-emerald-400'
                                          : 'bg-amber-500/20 text-amber-400'
                                      }`}>
                                        {e.recon_status === 'reconciled' ? 'Avstemt' : 'Åpen'}
                                      </span>
                                    </td>
                                    <td className="py-1.5 pr-2 text-center">
                                      <span className={`text-[10px] px-2 py-0.5 rounded-full ${
                                        isDark ? 'bg-slate-800 text-slate-400' : 'bg-slate-100 text-slate-500'
                                      }`}>{e.posting_count}</span>
                                    </td>
                                    <td className={`py-1.5 pr-2 text-right tabular-nums ${
                                      Number(e.total) < 0 ? (isDark ? 'text-red-400' : 'text-red-600') : ''
                                    }`}>{fmtAmount(e.total)}</td>
                                  </tr>
                                ))}
                                {/* Period subtotal row */}
                                <tr className={`border-b-2 ${section} ${isDark ? 'bg-slate-800/20' : 'bg-slate-50/60'}`}>
                                  <td className={`py-1.5 pr-2 pl-2 text-[10px] ${muted}`}>
                                    Sum {MONTH_FULL[pg.recon_month - 1]}{pg.recon_year !== year ? ` ${pg.recon_year}` : ''}
                                  </td>
                                  <td className="py-1.5 pr-2 text-center">
                                    <span className={`text-[10px] ${muted}`}>{pg.entries.length} avd.</span>
                                  </td>
                                  <td className="py-1.5 pr-2 text-center">
                                    <span className={`text-[10px] ${muted}`}>{pg.totalCount}</span>
                                  </td>
                                  <td className="py-1.5 pr-2 text-right tabular-nums font-semibold">{fmtAmount(pg.totalAmount)}</td>
                                </tr>
                              </React.Fragment>
                            ))}
                          </tbody>
                          <tfoot>
                            <tr className={`border-t-2 ${section}`}>
                              <td className={`py-1.5 pr-2 font-mono text-[10px] uppercase tracking-widest ${muted}`}>Totalt</td>
                              <td className="py-1.5 pr-2 text-center">
                                <span className={`text-[10px] ${muted}`}>{openEntries.length} avd.</span>
                              </td>
                              <td className="py-1.5 pr-2 text-center">
                                <span className={`text-[10px] ${muted}`}>{openEntries.reduce((s, e) => s + e.posting_count, 0)}</span>
                              </td>
                              <td className="py-1.5 pr-2 text-right tabular-nums font-semibold">{fmtAmount(openEntriesTotalAmount)}</td>
                            </tr>
                          </tfoot>
                        </table>
                      );
                    })()}
                  </div>
                )}
              </div>
            )}

            {/* General bulk action bar */}
            {rawData && !loadingData && (expandAll || expandedDept) && (
              <div className={`rounded-lg border ${card} p-2.5 mb-4 flex items-center gap-3 flex-wrap`}>
                <label className="flex items-center gap-1.5 cursor-pointer" onClick={e => e.stopPropagation()}>
                  <input
                    type="checkbox"
                    checked={allFilteredPostings.length > 0 && allFilteredPostings.every(p => selectedPostings.has(p.tripletex_id))}
                    ref={el => {
                      if (el) {
                        const someSelected = allFilteredPostings.some(p => selectedPostings.has(p.tripletex_id));
                        const allSelected = allFilteredPostings.length > 0 && allFilteredPostings.every(p => selectedPostings.has(p.tripletex_id));
                        el.indeterminate = someSelected && !allSelected;
                      }
                    }}
                    onChange={toggleAllVisible}
                    className="accent-sky-500 cursor-pointer"
                  />
                  <span className={`font-mono text-[10px] ${muted}`}>
                    Velg alle ({allFilteredPostings.length})
                  </span>
                </label>

                {hasSelection && (
                  <>
                    <span className={`font-mono text-[10px] ${isDark ? 'text-sky-400' : 'text-sky-600'}`}>
                      {selectedPostings.size} valgt
                    </span>
                    <select
                      value={bulkTarget}
                      onChange={e => setBulkTarget(e.target.value)}
                      className={`font-mono text-[10px] px-2 py-1 rounded border outline-none cursor-pointer ${
                        isDark ? 'bg-slate-800 border-slate-700 text-slate-300' : 'bg-white border-slate-300 text-slate-700'
                      }`}
                    >
                      <option value="">Flytt til…</option>
                      {[year - 1, year, year + 1].map(y =>
                        MONTHS.map((m, i) => (
                          <option key={`${y}_${i + 1}`} value={`${y}_${i + 1}`}>
                            {m} {y}
                          </option>
                        ))
                      )}
                    </select>
                    <button
                      onClick={handleBulkMove}
                      disabled={!bulkTarget || bulkSaving}
                      className={`font-mono text-[10px] px-3 py-1 rounded transition-colors disabled:opacity-40 ${
                        isDark ? 'bg-sky-600 hover:bg-sky-500 text-white' : 'bg-sky-500 hover:bg-sky-400 text-white'
                      }`}
                    >
                      {bulkSaving ? 'Flytter…' : `Flytt ${selectedPostings.size}`}
                    </button>
                  </>
                )}
              </div>
            )}

            {/* Department table */}
            {rawData && !loadingData && deptRows.length > 0 && (
              <div className={`rounded-xl border ${card} overflow-hidden`}>
                <table className="w-full font-mono text-[11px] border-collapse">
                  <thead>
                    <tr className={`border-b ${section} ${muted}`}>
                      <th className="text-left py-2.5 px-4 font-normal">Avdeling</th>
                      {effMonth !== null && (
                        <th className="text-center py-2.5 px-2 font-normal">Status</th>
                      )}
                      <th className="text-right py-2.5 px-2 font-normal">Kontant (+)</th>
                      <th className="text-right py-2.5 px-2 font-normal">Utbetalinger (−)</th>
                      <th className="text-right py-2.5 px-2 font-normal">Kontantsvinn</th>
                      <th className="text-right py-2.5 px-2 font-normal">Netto</th>
                      <th className="text-center py-2.5 px-2 font-normal">Poster</th>
                      <th className="py-2.5 px-4"></th>
                    </tr>
                  </thead>
                  <tbody>
                    {deptRows.map(row => {
                      const deptId = row.department_tripletex_id;
                      const isExp = expandAll || (
                        expandedDept?.department_tripletex_id === deptId
                        && (effMonth === null || expandedDept?.recon_month === row.recon_month)
                      );
                      const reconciled = effMonth !== null && isReconciled(deptId, effMonth);
                      const deptPostings = isExp ? getPostingsForDept(deptId) : [];
                      const deptGrouped = isExp && groupByDesc ? groupPostingsByDesc(deptPostings) : [];

                      return (
                        <DeptRow
                          key={`${deptId}_${row.recon_month ?? 'all'}`}
                          row={row}
                          isExpanded={isExp}
                          reconciled={reconciled}
                          selectedMonth={effMonth}
                          onToggle={() => handleToggleDept(row)}
                          resolveDept={resolveDept}
                          fmtAmount={fmtAmount}
                          isDark={isDark}
                          section={section}
                          muted={muted}
                          rowHover={rowHover}
                          subRow={subRow}
                          expandedPostings={deptPostings}
                          resolveContact={resolveContact}
                          year={year}
                          onMonthOverride={handleMonthOverride}
                          onToggleRecon={handleToggleRecon}
                          savingOverride={savingOverride}
                          savingRecon={savingRecon}
                          periodMap={periodMap}
                          fadingOut={fadingOut}
                          selectedPostings={selectedPostings}
                          onTogglePosting={togglePosting}
                          onToggleAll={() => toggleAllInDept(deptPostings)}
                          hasSelection={hasSelection}
                          bulkTarget={bulkTarget}
                          setBulkTarget={setBulkTarget}
                          onBulkMove={handleBulkMove}
                          bulkSaving={bulkSaving}
                          groupByDesc={groupByDesc}
                          groupedPostings={deptGrouped}
                          onBookCashLoss={handleBookCashLoss}
                          bookingCashLoss={bookingCashLoss}
                        />
                      );
                    })}
                  </tbody>
                  <tfoot>
                    <tr className={`border-t-2 ${section}`}>
                      <td className={`py-2.5 px-4 font-mono text-[10px] uppercase tracking-widest ${muted}`}>
                        Sum{ytd && selectedMonth !== null
                          ? ` t.o.m. ${MONTH_FULL[selectedMonth - 1]}`
                          : singleMonth
                            ? ` ${MONTH_FULL[selectedMonth - 1]}`
                            : ` ${year}`}
                      </td>
                      {effMonth !== null && <td />}
                      <td className="py-2.5 px-2 text-right tabular-nums font-semibold">
                        {fmtAmount(deptRows.reduce((s, r) => s + r.total_debit, 0))}
                      </td>
                      <td className="py-2.5 px-2 text-right tabular-nums font-semibold text-white">
                        {fmtAmount(deptRows.reduce((s, r) => s + r.total_credit - (r.app_booked_total || 0), 0))}
                      </td>
                      <td className={`py-2.5 px-2 text-right tabular-nums font-semibold ${
                        deptRows.reduce((s, r) => s + (r.app_booked_total || 0), 0) < 0
                          ? (isDark ? 'text-red-400' : 'text-red-600')
                          : 'text-white'
                      }`}>
                        {fmtAmount(deptRows.reduce((s, r) => s + (r.app_booked_total || 0), 0)) || '—'}
                      </td>
                      <td className={`py-2.5 px-2 text-right tabular-nums font-semibold ${
                        deptRows.reduce((s, r) => s + r.total, 0) > 0
                          ? (isDark ? 'text-red-400' : 'text-red-600')
                          : 'text-white'
                      }`}>
                        {fmtAmount(deptRows.reduce((s, r) => s + r.total, 0))}
                      </td>
                      <td className="py-2.5 px-2 text-center">
                        <span className={`font-mono text-[10px] ${muted}`}>
                          {deptRows.reduce((s, r) => s + r.posting_count, 0)}
                        </span>
                      </td>
                      <td />
                    </tr>
                  </tfoot>
                </table>
              </div>
            )}

            {!loadingData && rawData && deptRows.length === 0 && (
              <p className={`font-mono text-xs ${muted}`}>
                Ingen åpne poster på konto 1900 for {selectedMonth !== null ? MONTH_FULL[selectedMonth - 1] : ''} {year}.
              </p>
            )}
          </div>
        )}
      </main>
    </div>
  );
}

/* ── Department row ──────────────────────────────────────── */
function DeptRow({
  row, isExpanded, reconciled, selectedMonth, onToggle,
  resolveDept, fmtAmount, isDark, section, muted, rowHover, subRow,
  expandedPostings, resolveContact, year,
  onMonthOverride, onToggleRecon, savingOverride, savingRecon, periodMap,
  fadingOut, selectedPostings, onTogglePosting, onToggleAll,
  hasSelection, bulkTarget, setBulkTarget, onBulkMove, bulkSaving,
  groupByDesc, groupedPostings,
  onBookCashLoss, bookingCashLoss,
}) {
  const deptId = row.department_tripletex_id;
  const allChecked = expandedPostings.length > 0 && expandedPostings.every(p => selectedPostings.has(p.tripletex_id));
  const someChecked = expandedPostings.some(p => selectedPostings.has(p.tripletex_id));

  return (
    <>
      <tr
        className={`border-b ${section} cursor-pointer transition-colors ${rowHover}`}
        onClick={onToggle}
      >
        <td className="py-2 px-4">{resolveDept(deptId)}</td>
        {selectedMonth !== null && (
          <td className="py-2 px-2 text-center">
            <span className={`text-[9px] px-2 py-0.5 rounded-full font-mono ${
              reconciled
                ? 'bg-emerald-500/20 text-emerald-400'
                : 'bg-amber-500/20 text-amber-400'
            }`}>
              {reconciled ? 'Avstemt' : 'Uavstemt'}
            </span>
          </td>
        )}
        <td className="py-2 px-2 text-right tabular-nums">{fmtAmount(row.total_debit)}</td>
        <td className="py-2 px-2 text-right tabular-nums text-white">
          {fmtAmount(row.total_credit - (row.app_booked_total || 0))}
        </td>
        <td className={`py-2 px-2 text-right tabular-nums ${
          row.app_booked_total < 0 ? (isDark ? 'text-red-400' : 'text-red-600') : 'text-white'
        }`}>
          {row.app_booked_total ? fmtAmount(row.app_booked_total) : '—'}
        </td>
        <td className={`py-2 px-2 text-right tabular-nums font-semibold ${
          row.total > 0 ? (isDark ? 'text-red-400' : 'text-red-600') : 'text-white'
        }`}>
          {fmtAmount(row.total)}
        </td>
        <td className="py-2 px-2 text-center">
          <span className={`font-mono text-[10px] px-2 py-0.5 rounded-full ${
            isDark ? 'bg-slate-800 text-slate-400' : 'bg-slate-100 text-slate-500'
          }`}>
            {row.posting_count}
          </span>
        </td>
        <td className="py-2 px-4 text-right">
          <span className={`text-[10px] ${muted}`}>{isExpanded ? '▲' : '▼'}</span>
        </td>
      </tr>

      {isExpanded && (
        <tr>
          <td colSpan={selectedMonth !== null ? 8 : 7} className="p-0">
            <div className={`${subRow} px-6 py-4`}>
              <div className="flex items-center justify-between mb-2 gap-2 flex-wrap">
                <p className={`font-mono text-[10px] uppercase tracking-widest ${muted}`}>
                  Posteringer ({expandedPostings.length})
                </p>

                <div className="flex items-center gap-2 flex-wrap">
                  {/* Bulk move controls */}
                  {hasSelection && (
                    <div className="flex items-center gap-1.5" onClick={e => e.stopPropagation()}>
                      <span className={`font-mono text-[10px] ${isDark ? 'text-sky-400' : 'text-sky-600'}`}>
                        {selectedPostings.size} valgt
                      </span>
                      <select
                        value={bulkTarget}
                        onChange={e => setBulkTarget(e.target.value)}
                        className={`text-[10px] px-1 py-0.5 rounded border outline-none cursor-pointer ${
                          isDark ? 'bg-slate-800 border-slate-700 text-slate-300' : 'bg-white border-slate-300 text-slate-700'
                        }`}
                      >
                        <option value="">Flytt til…</option>
                        {[year - 1, year, year + 1].map(y =>
                          MONTHS.map((m, i) => (
                            <option key={`${y}_${i + 1}`} value={`${y}_${i + 1}`}>
                              {m} {y}
                            </option>
                          ))
                        )}
                      </select>
                      <button
                        onClick={onBulkMove}
                        disabled={!bulkTarget || bulkSaving}
                        className={`font-mono text-[10px] px-2.5 py-0.5 rounded transition-colors disabled:opacity-40 ${
                          isDark ? 'bg-sky-600 hover:bg-sky-500 text-white' : 'bg-sky-500 hover:bg-sky-400 text-white'
                        }`}
                      >
                        {bulkSaving ? 'Flytter…' : `Flytt ${selectedPostings.size}`}
                      </button>
                    </div>
                  )}

                  {selectedMonth !== null && Math.abs(row.total) > 0.005 && (
                    <button
                      onClick={(e) => {
                        e.stopPropagation();
                        onBookCashLoss(deptId, expandedPostings, row.total_debit, row.total_credit);
                      }}
                      disabled={bookingCashLoss === deptId}
                      className={`font-mono text-[10px] px-3 py-1 rounded transition-colors disabled:opacity-40 ${
                        isDark
                          ? 'bg-amber-600/20 text-amber-400 hover:bg-amber-600/30 border border-amber-500/30'
                          : 'bg-amber-50 text-amber-700 hover:bg-amber-100 border border-amber-300'
                      }`}
                    >
                      {bookingCashLoss === deptId
                        ? 'Bokfører…'
                        : `Bokfør kontantsvinn ${fmtAmount(row.total)}`}
                    </button>
                  )}

                  {selectedMonth !== null && (
                    <button
                      onClick={(e) => { e.stopPropagation(); onToggleRecon(deptId, selectedMonth, row.total); }}
                      disabled={savingRecon === `${deptId}_${selectedMonth}` || (!reconciled && Math.abs(row.total) > 0.005)}
                      title={!reconciled && Math.abs(row.total) > 0.005 ? 'Netto må være 0 for å avstemme' : ''}
                      className={`font-mono text-[10px] px-3 py-1 rounded transition-colors disabled:opacity-40 ${
                        reconciled
                          ? 'bg-emerald-500/20 text-emerald-400 hover:bg-emerald-500/30'
                          : isDark
                            ? 'bg-slate-700 text-slate-300 hover:bg-slate-600'
                            : 'bg-slate-200 text-slate-700 hover:bg-slate-300'
                      }`}
                    >
                      {savingRecon === `${deptId}_${selectedMonth}`
                        ? 'Lagrer...'
                        : reconciled ? '✓ Avstemt — klikk for å åpne' : 'Merk som avstemt'}
                    </button>
                  )}
                </div>
              </div>

              {groupByDesc ? (
                <table className="w-full font-mono text-[11px]">
                  <thead>
                    <tr className={`${muted} border-b ${section}`}>
                      <th className="text-left py-1 pr-2 font-normal">Beskrivelse</th>
                      <th className="text-center py-1 pr-2 font-normal">Poster</th>
                      <th className="text-right py-1 pr-2 font-normal">Sum</th>
                    </tr>
                  </thead>
                  <tbody>
                    {groupedPostings.map(g => (
                      <tr key={g.description} className={`border-b ${section}`}>
                        <td className="py-1.5 pr-2">{g.description}</td>
                        <td className="py-1.5 pr-2 text-center">
                          <span className={`text-[10px] px-2 py-0.5 rounded-full ${
                            isDark ? 'bg-slate-800 text-slate-400' : 'bg-slate-100 text-slate-500'
                          }`}>{g.count}</span>
                        </td>
                        <td className={`py-1.5 pr-2 text-right tabular-nums ${
                          g.total < 0 ? (isDark ? 'text-red-400' : 'text-red-600') : ''
                        }`}>{fmtAmount(g.total)}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              ) : (
                <table className="w-full font-mono text-[11px]">
                  <thead>
                    <tr className={`${muted} border-b ${section}`}>
                      <th className="py-1 pr-1 w-6 font-normal" onClick={e => e.stopPropagation()}>
                        <input
                          type="checkbox"
                          checked={allChecked}
                          ref={el => { if (el) el.indeterminate = someChecked && !allChecked; }}
                          onChange={onToggleAll}
                          className="accent-sky-500 cursor-pointer"
                        />
                      </th>
                      <th className="text-left py-1 pr-2 font-normal">Dato</th>
                      <th className="text-left py-1 pr-2 font-normal">Bilagsnr</th>
                      <th className="text-left py-1 pr-2 font-normal">Beskrivelse</th>
                      <th className="text-left py-1 pr-2 font-normal">Kunde / Lev.</th>
                      <th className="text-right py-1 pr-2 font-normal">Beløp</th>
                      <th className="text-center py-1 pr-2 font-normal">Måned</th>
                      <th className="text-center py-1 font-normal">Dok</th>
                    </tr>
                  </thead>
                  <tbody>
                    {expandedPostings.map(p => {
                      const isSaving = savingOverride === p.tripletex_id;
                      const isFading = fadingOut.has(p.tripletex_id);
                      const isChecked = selectedPostings.has(p.tripletex_id);
                      return (
                        <tr
                          key={p.id}
                          className={`border-b ${section} transition-all duration-300 ${
                            isFading ? 'opacity-0 max-h-0 overflow-hidden' : 'opacity-100'
                          }`}
                          style={isFading ? { lineHeight: 0, padding: 0 } : undefined}
                        >
                          <td className="py-1 pr-1 w-6" onClick={e => e.stopPropagation()}>
                            <input
                              type="checkbox"
                              checked={isChecked}
                              onChange={() => onTogglePosting(p.tripletex_id)}
                              className="accent-sky-500 cursor-pointer"
                            />
                          </td>
                          <td className="py-1 pr-2">
                            {p.date ? new Date(p.date).toLocaleDateString('nb-NO') : '—'}
                          </td>
                          <td className="py-1 pr-2">{p.voucher_number ?? '—'}</td>
                          <td className="py-1 pr-2 truncate max-w-[200px]" title={p.description ?? ''}>
                            {p.description ?? '—'}
                            {p.has_override && (
                              <span className="ml-1 text-[8px] px-1 py-0.5 rounded bg-sky-500/20 text-sky-400" title={`Opprinnelig: ${MONTH_FULL[p.natural_month - 1]} ${p.natural_year}`}>
                                flyttet
                              </span>
                            )}
                            {p.booked_by_app && (
                              <span className="ml-1 text-[8px] px-1 py-0.5 rounded bg-amber-500/20 text-amber-400">
                                kontantsvinn
                              </span>
                            )}
                          </td>
                          <td className="py-1 pr-2 truncate max-w-[120px]">
                            {resolveContact(p.customer_tripletex_id, p.supplier_tripletex_id) ?? '—'}
                          </td>
                          <td className={`py-1 pr-2 text-right tabular-nums ${
                            Number(p.amount) < 0 ? (isDark ? 'text-red-400' : 'text-red-600') : ''
                          }`}>
                            {fmtAmount(p.amount)}
                          </td>
                          <td className="py-1 pr-2 text-center" onClick={e => e.stopPropagation()}>
                            <select
                              value={`${p.recon_year}_${p.recon_month}`}
                              onChange={e => {
                                const [y, m] = e.target.value.split('_').map(Number);
                                onMonthOverride(p, y, m);
                              }}
                              disabled={isSaving}
                              className={`text-[10px] px-1 py-0.5 rounded border outline-none cursor-pointer ${
                                isDark ? 'bg-slate-800 border-slate-700 text-slate-300' : 'bg-white border-slate-300 text-slate-700'
                              } ${isSaving ? 'opacity-40' : ''} ${p.has_override ? 'border-sky-500/50' : ''}`}
                            >
                              {[year - 1, year, year + 1].map(y =>
                                MONTHS.map((m, i) => (
                                  <option key={`${y}_${i + 1}`} value={`${y}_${i + 1}`}>
                                    {m} {y}
                                  </option>
                                ))
                              )}
                            </select>
                          </td>
                          <td className="py-1 text-center">
                            <DocButtons attachmentPath={p.attachment_path} ediDocumentPath={p.edi_document_path} />
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              )}
            </div>
          </td>
        </tr>
      )}
    </>
  );
}

/* ── Doc buttons ─────────────────────────────────────────── */
function DocButtons({ attachmentPath, ediDocumentPath }) {
  async function openDoc(path) {
    const { data, error } = await supabase.storage
      .from('voucher-documents')
      .createSignedUrl(path, 3600);
    if (error || !data?.signedUrl) return;
    window.open(data.signedUrl, '_blank');
  }

  if (!attachmentPath && !ediDocumentPath) return <span className="text-slate-600">—</span>;

  return (
    <span className="inline-flex gap-1" onClick={e => e.stopPropagation()}>
      {attachmentPath && (
        <button
          onClick={() => openDoc(attachmentPath)}
          className="text-[9px] font-mono px-1.5 py-0.5 rounded bg-red-500/20 text-red-400 hover:bg-red-500/30 transition-colors"
          title="Vis PDF"
        >
          PDF
        </button>
      )}
      {ediDocumentPath && (
        <button
          onClick={() => openDoc(ediDocumentPath)}
          className="text-[9px] font-mono px-1.5 py-0.5 rounded bg-sky-500/20 text-sky-400 hover:bg-sky-500/30 transition-colors"
          title="Vis XML (EHF)"
        >
          XML
        </button>
      )}
    </span>
  );
}
